import {
  useCurrentUserContext,
  usePermissions,
  useUserTeams,
} from '@openclassrooms/data-connect-web/users';
import { useOrganizationPermissions } from '@openclassrooms/data-connect-web/organizations';
import { useGetProfileCompletion } from '@openclassrooms/data-connect-web/users';

import { useTeamsPermissions } from '@openclassrooms/data-connect-web/teams';
import { ROUTES, getPathName } from '@openclassrooms/routing';
import type { User as ApiUser } from '@openclassrooms/sdk-web';

type HeaderConfiguration = {
  user?: User;
  right: Right;
  isConnected: boolean;
};

interface User {
  id?: number | null;
  displayableName?: string;
  profilePicture?: string;
  profileCompletionRate?: number | null;
  organizationName?: string;
  link: {
    profile?: string;
    logOut?: string;
    employerSpace?: string;
    parameter?: string;
  };
}

interface Right {
  canSeeMentorDashboard: boolean;
  canSeeAdminDashboard: boolean;
  canSeeTutorDashboard: boolean;
  canSeeOrganizationSpace: boolean;
}

function useEmployerSpaceLink(user?: ApiUser | null) {
  const { can } = usePermissions({}, { retry: 0, enabled: Boolean(user?.id) });
  const { can: canOrganization } = useOrganizationPermissions(
    { organizationId: user?.organization?.id as string },
    { enabled: Boolean(user?.organization?.id), retry: 0 }
  );
  const { can: canTeam } = useTeamsPermissions({}, { enabled: Boolean(user?.id), retry: 0 });
  const { data: userTeams = { entities: [] } } = useUserTeams(
    { userId: user?.id as number },
    { enabled: Boolean(user?.id), retry: 0 }
  );

  if (!user) {
    return '';
  }
  if (!user?.organization?.id) {
    return '';
  }
  const organizationParams = { organizationId: user.organization.id };
  if (can('ACCESS_TALENTS_MARKETPLACE')) {
    return getPathName(ROUTES.ORGANIZATIONS.TALENTS, organizationParams, false);
  }
  if (canOrganization('GET_ORGANIZATION_USERS')) {
    return getPathName(ROUTES.ORGANIZATIONS.USERS, organizationParams, false);
  }
  if (canOrganization('ACCESS_ORGANIZATION_ANALYTICS')) {
    return getPathName(ROUTES.ORGANIZATIONS.ANALYTICS, organizationParams, false);
  }
  const teamId = userTeams.entities.find((team) => {
    if (!team.id) {
      return false;
    }
    return canTeam('ACCESS_TEAM_ANALYTICS', team.id);
  })?.id;
  if (teamId) {
    return getPathName(ROUTES.TEAMS.ANALYTICS, { teamId }, false);
  }
  return '';
}

function useHeaderConfiguration(): HeaderConfiguration {
  const { currentUser: user, isConnected } = useCurrentUserContext();
  const { data: userProfileCompletionRate } = useGetProfileCompletion(
    { userId: user?.id! },
    { enabled: Boolean(user?.id) }
  );

  const employerSpaceLink = useEmployerSpaceLink(user);

  return {
    user: user
      ? {
          id: user.id,
          displayableName: user.displayName,
          organizationName: user.organization?.name,
          profilePicture: user.profilePicture,
          profileCompletionRate: userProfileCompletionRate?.completion,
          link: {
            profile: getPathName(ROUTES.MEMBERS.SHOW, { id: user.publicId }),
            employerSpace: employerSpaceLink,
            parameter: getPathName(ROUTES.MEMBERS.PARAMETERS, { id: user.publicId }),
          },
        }
      : undefined,
    // TODO : use permission API to get user rights
    right: {
      canSeeAdminDashboard: false,
      canSeeMentorDashboard: false,
      canSeeTutorDashboard: false,
      canSeeOrganizationSpace: false,
    },
    isConnected,
  };
}

export default useHeaderConfiguration;
